/* global DetailsDisclosure, trapFocus, removeTrapFocus */
/* global SideDrawer */
/* global Shopify, theme */

/**
 * Independent script loader for cart drawer only.
 * Does NOT conflict with existing loadScript in theme.
 */
function cartDrawerLoadScript(src) {
  return new Promise((resolve, reject) => {
    if (!src) return resolve();

    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === 'true') return resolve();

      existing.addEventListener('load', resolve, { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }

    const s = document.createElement('script');
    s.src = src;
    s.defer = true;

    s.addEventListener('load', () => {
      s.dataset.loaded = 'true';
      resolve();
    }, { once: true });

    s.addEventListener('error', reject, { once: true });

    document.body.appendChild(s);
  });
}

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      const drawer = document.getElementById('cart-drawer');
      this.overlay = drawer ? drawer.querySelector('.overlay') : null;

      this._handleClick = this._handleClick.bind(this);
      this._handleKeyup = this._handleKeyup.bind(this);
    }

    _handleClick(evt) {
      if (evt.target.matches('.js-close') || evt.currentTarget === this.overlay) {
        this.close();
      }
    }

    _handleKeyup(evt) {
      if (evt.key === 'Escape') this.close();
    }

    open() {
      if (this.overlay) this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      this.addEventListener('click', this._handleClick);
      this.addEventListener('keyup', this._handleKeyup);
      if (this.overlay) this.overlay.addEventListener('click', this._handleClick);
    }

    close() {
      if (this.overlay) this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this._handleClick);
      this.removeEventListener('keyup', this._handleKeyup);
      if (this.overlay) this.overlay.removeEventListener('click', this._handleClick);
    }
  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.isStickyCheckout = this.cartSummary
        ? this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')
        : false;

      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      if (this.openDelayTimer) clearTimeout(this.openDelayTimer);
    }

    init() {
      const cartIcon = document.getElementById('cart-icon');
      if (!cartIcon) return;

      cartIcon.setAttribute('role', 'button');
      cartIcon.setAttribute('aria-haspopup', 'dialog');

      cartIcon.addEventListener('click', (evt) => {
        evt.preventDefault();
        this.open(cartIcon);
      });

      cartIcon.addEventListener('keydown', (evt) => {
        if (evt.key !== ' ') return;
        evt.preventDefault();
        this.open(cartIcon);
      });
    }

    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.isStickyCheckout) {
        this.dcbLoadedHandler = CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler, { passive: true });
      }

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);
    }

    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();
    }

    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    open(opener, elementToFocus) {
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      const delay = quickAddDrawer ? 200 : 0;

      if (this.openDelayTimer) clearTimeout(this.openDelayTimer);
      this.openDelayTimer = setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          if (this.isStickyCheckout) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.isStickyCheckout) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';
      }, delay);

      if (typeof window.initLazyImages === 'function') window.initLazyImages();
    }

    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector('cart-items');
        if (cartItems && !dontRefreshCartItems) {
          cartItems.refresh();
        } else {
          const response = this.getSectionsToRender().map((section) => section.section);
          const cartResponse = await fetch(`?sections=${response.join(',')}`);
          const sections = await cartResponse.json();
          this.renderContents({ sections }, false);
        }
      } catch (error) {
        console.log(error);
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: { error: this.errorMsg ? this.errorMsg.textContent : '' }
        }));
      }
    }

    async renderContents(data, openDrawer = true) {
      if (!this.scriptsLoaded) {
        const promises = [];

        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          promises.push(cartDrawerLoadScript(theme.scripts.cartItems));
        }

        if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) && this.dataset.shippingCalculator) {
          promises.push(cartDrawerLoadScript(theme.scripts.countryProvinceSelector));
          promises.push(cartDrawerLoadScript(theme.scripts.shippingCalculator));
        }

        if (promises.length) await Promise.all(promises);

        this.scriptsLoaded = true;
      }

      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          el.innerHTML = CartDrawer.getElementHTML(
            data.sections[section.section],
            section.selector
          );
        }
      });

      if (openDrawer && this.getAttribute('open') === null) {
        setTimeout(() => this.open(), 0);
      }

      if (typeof window.initLazyImages === 'function') window.initLazyImages();
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;
      const node = tmpl.content.querySelector(selector);
      return node ? node.innerHTML : '';
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}
